import {Info} from "lucide-react";
import {createContext} from "react";

import {Button} from "@/components/ui/button";
import {
  Table as SchTable,
  TableBody as SchTableBody,
  TableCell as SchTableCell,
  TableHead as SchTableHead,
  TableHeader as SchTableHeader,
  TableRow as SchTableRow,
} from "@/components/ui/sch-table";
import {Skeleton as SchSkeleton} from "@/components/ui/skeleton";
import {cn} from "@/lib/utils";

import {Pagination} from "./pagination";

const TableContext = createContext({});
TableContext.displayName = "TableContext";

const ACTION_VARIANTS = {
  delete: "text-destructive hover:text-destructive/90 hover:bg-destructive/10",
  edit: "text-amber-600 hover:text-amber-800 hover:bg-amber-100",
  details: "text-primary hover:text-primary/90 hover:bg-primary/10",
  activate: "text-green-600 hover:text-green-800 hover:bg-green-100",
  enable: "text-green-600 hover:text-green-800 hover:bg-green-100",
  disable: "text-amber-700 hover:text-amber-900 hover:bg-amber-100",
  "change-password": "text-muted-foreground hover:text-foreground hover:bg-muted",
  transfer: "text-muted-foreground hover:text-foreground hover:bg-muted",
};

type TActionButtonProps = {
  action: keyof typeof ACTION_VARIANTS;
  variant: keyof typeof ACTION_VARIANTS;
  className?: string;
  onClick: () => void;
  disabled?: boolean;
  icon?: React.ElementType;
  title?: string;
  helpTarget?: string;
};

function ActionButton({
  action,
  variant,
  className,
  onClick,
  disabled,
  icon: Icon = Info,
  title,
  helpTarget,
}: TActionButtonProps) {
  const helpAttrs = helpTarget ? {"data-help-target": helpTarget} : undefined;

  const button = (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        ACTION_VARIANTS[variant],
        {
          "text-muted-foreground opacity-50": disabled,
        },
        "shrink-0 self-start",
        className,
      )}
      disabled={disabled}
      onClick={onClick}
      title={disabled ? undefined : title}
      {...(!disabled ? helpAttrs : {})}
    >
      <Icon className="size-4" />
      <span className="sr-only capitalize">{action}</span>
    </Button>
  );

  if (disabled && (title || helpTarget)) {
    return (
      <span className="inline-flex shrink-0 self-start" title={title} {...helpAttrs}>
        {button}
      </span>
    );
  }

  return button;
}

type TActionsCellProps = {
  children: React.ReactNode;
  className?: string;
  helpTarget?: string;
};

function ActionsCell({children, className, helpTarget}: TActionsCellProps) {
  return (
    <SchTableCell
      className={cn("!align-top !pt-1 !pb-1 text-right whitespace-nowrap", className)}
    >
      <div
        className="flex h-fit w-full items-start justify-end gap-1 md:gap-2"
        {...(helpTarget ? {"data-help-target": helpTarget} : {})}
      >
        {children}
      </div>
    </SchTableCell>
  );
}

type TTableCellProps = {
  children: React.ReactNode;
  center?: boolean;
  className?: string;
};

function TableCell({
  children,
  className,
  center,
  ...props
}: TTableCellProps & React.ComponentProps<"td">) {
  return (
    <SchTableCell
      className={cn("text-foreground whitespace-nowrap", className, center && "text-center")}
      {...props}
    >
      {children}
    </SchTableCell>
  );
}

type TTableRowProps = {
  children: React.ReactNode;
  className?: string;
};

function TableRow({children, className}: TTableRowProps) {
  return <SchTableRow className={cn("hover:bg-muted/50", className)}>{children}</SchTableRow>;
}

type TTableHeadProps = {
  center?: boolean;
  children: React.ReactNode;
  className?: string;
};

function TableHead({children, center, className}: TTableHeadProps) {
  return (
    <SchTableHead className={cn("whitespace-nowrap", className, center && "text-center")}>
      {children}
    </SchTableHead>
  );
}

type TActionsHeadProps = {
  children?: React.ReactNode;
  className?: string;
};

function ActionsHead({children, className}: TActionsHeadProps) {
  return (
    <SchTableHead className={cn("w-[170px] pr-4 text-right whitespace-nowrap", className)}>
      {children}
    </SchTableHead>
  );
}

type TTableHeaderProps = {
  children: React.ReactNode;
  className?: string;
};

function TableHeader({children, className}: TTableHeaderProps) {
  return (
    <SchTableHeader
      className={cn(
        "[&>tr>th]:bg-gray-200",
        "[&>tr>th:first-child]:rounded-l-lg",
        "[&>tr>th:last-child]:rounded-r-lg",
        className,
      )}
    >
      <SchTableRow>{children}</SchTableRow>
    </SchTableHeader>
  );
}

type TWrapperProps = {
  children: React.ReactNode;
};

function Wrapper({children}: TWrapperProps) {
  return <div className="bg-background overflow-hidden rounded-lg p-2 shadow-sm">{children}</div>;
}

type TTableProps = {
  children: React.ReactNode;
  className?: string;
};

function Table({children, className}: TTableProps) {
  return (
    <div className="overflow-x-auto">
      <SchTable className={cn(className)}>{children}</SchTable>
    </div>
  );
}

type TSkeletonProps = {
  size: number;
  withActions: boolean;
};

function Skeleton({size, withActions}: TSkeletonProps) {
  return (
    <SchTableRow>
      {new Array(size).fill("").map((_, index) => (
        <SchTableCell key={`skl-${index}`} className="text-foreground whitespace-nowrap">
          <SchSkeleton className="h-4 w-full" />
        </SchTableCell>
      ))}
      {withActions && <ActionSkeleton />}
    </SchTableRow>
  );
}

type TActionSkeletonProps = {
  className?: string;
};

function ActionSkeleton({className}: TActionSkeletonProps) {
  return (
    <ActionsCell className={cn("text-right whitespace-nowrap", className)}>
      <SchSkeleton className="size-8" />
      <SchSkeleton className="size-8" />
      <SchSkeleton className="size-8" />
    </ActionsCell>
  );
}

type TEmptyProps = {
  children: React.ReactNode;
  colSpan?: number;
};

function Empty({children, colSpan = 10}: TEmptyProps) {
  return (
    <Table.Row>
      <Table.Cell colSpan={colSpan} className="text-muted-foreground py-6 text-center">
        {children}
      </Table.Cell>
    </Table.Row>
  );
}

Table.Head = TableHead;
Table.Header = TableHeader;
Table.Body = SchTableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.ActionButton = ActionButton;
Table.ActionsCell = ActionsCell;
Table.Pagination = Pagination;
Table.Wrapper = Wrapper;
Table.ActionsHead = ActionsHead;
Table.Skeleton = Skeleton;
Table.Empty = Empty;
Table.ActionSkeleton = ActionSkeleton;

export default Table;
